import { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  Button,
  Drawer,
  FieldError,
  Form,
  Input,
  Label,
  ListBox,
  Select,
  Switch,
  TextArea,
  TextField,
  toast,
} from "@heroui/react";

import { useCreateBanner, useUpdateBanner } from "../use-banners";
import { BannerEntity, CreateBannerRequest } from "../types";

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  banner?: BannerEntity;
};

interface IFormInput {
  title: string;
  description: string;
  type: string;
  category: string;
  label: string;
  url: string;
  image: string;
  isActive: boolean;
}

const BANNER_TYPES = [
  { key: "main", title: "Главный" },
  { key: "terms", title: "Условия доставки" },
];

const BANNER_CATEGORIES = [
  { key: "internal", title: "Внутренняя ссылка" },
  { key: "external", title: "Внешняя ссылка" },
  { key: "web", title: "Web (WebView)" },
];

export function BannerDrawer({ isOpen, onOpenChange, mode, banner }: Props) {
  const createMutation = useCreateBanner();
  const updateMutation = useUpdateBanner();

  const isPending = createMutation.isPending || updateMutation.isPending;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<IFormInput>({
    defaultValues: { isActive: true },
  });

  useEffect(() => {
    if (isOpen && mode === "edit" && banner) {
      reset({
        title: banner.title,
        description: banner.description ?? "",
        type: banner.type ? banner.type.toLowerCase() : "",
        category: banner.category ? banner.category.toLowerCase() : "",
        label: banner.label ?? "",
        url: banner.url ?? "",
        image: banner.image ?? "",
        isActive: banner.isActive,
      });
    } else if (!isOpen) {
      reset({ isActive: true });
    }
  }, [isOpen, mode, banner, reset]);

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    const payload: CreateBannerRequest = {
      title: data.title.trim(),
      description: data.description,
      type: data.type.trim(),
      category: data.category,
      label: data.label,
      url: data.url,
      image: data.image,
      isActive: data.isActive,
    };

    if (mode === "create") {
      createMutation
        .mutateAsync(payload)
        .then(() => {
          toast.success("Баннер создан");
          onOpenChange(false);
        })
        .catch(() => {
          toast.danger("Ошибка при создании баннера");
        });
    } else if (banner) {
      updateMutation
        .mutateAsync({ ...payload, id: banner.id })
        .then(() => {
          toast.success("Баннер обновлён");
          onOpenChange(false);
        })
        .catch(() => {
          toast.danger("Ошибка при обновлении баннера");
        });
    }
  };

  const isActive = watch("isActive");

  return (
    <Drawer isOpen={isOpen} onOpenChange={onOpenChange}>
      <Drawer.Backdrop>
        <Drawer.Content placement="right">
          <Drawer.Dialog>
            <Form
              className="flex h-full flex-col"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Drawer.Header>
                <Drawer.Heading>
                  {mode === "create" ? "Создать баннер" : "Изменить баннер"}
                </Drawer.Heading>
              </Drawer.Header>

              <Drawer.Body className="flex flex-1 flex-col gap-3 overflow-y-auto">
                <Controller
                  control={control}
                  name="title"
                  render={({ field }) => (
                    <TextField
                      isInvalid={!!errors.title?.message}
                      value={field.value ?? ""}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                    >
                      <Label>Название *</Label>
                      <Input ref={field.ref} placeholder="Введите название" />
                      <FieldError>{errors.title?.message}</FieldError>
                    </TextField>
                  )}
                  rules={{ required: "Обязательное поле" }}
                />

                <Controller
                  control={control}
                  name="description"
                  render={({ field }) => (
                    <TextField
                      value={field.value ?? ""}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                    >
                      <Label>Описание</Label>
                      <TextArea
                        ref={field.ref}
                        placeholder="Введите описание"
                        rows={3}
                      />
                    </TextField>
                  )}
                />

                <Controller
                  control={control}
                  name="type"
                  render={({ field }) => (
                    <Select
                      isInvalid={!!errors.type?.message}
                      selectedKey={field.value || null}
                      onSelectionChange={(key) => field.onChange(key)}
                    >
                      <Label>Тип *</Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <FieldError>{errors.type?.message}</FieldError>
                      <Select.Popover>
                        <ListBox items={BANNER_TYPES}>
                          {({ key, title }) => (
                            <ListBox.Item id={key} textValue={title}>
                              {title}
                            </ListBox.Item>
                          )}
                        </ListBox>
                      </Select.Popover>
                    </Select>
                  )}
                  rules={{ required: "Обязательное поле" }}
                />

                <Controller
                  control={control}
                  name="category"
                  render={({ field }) => (
                    <Select
                      selectedKey={field.value || null}
                      onSelectionChange={(key) => field.onChange(key)}
                    >
                      <Label>Категория</Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox items={BANNER_CATEGORIES}>
                          {({ key, title }) => (
                            <ListBox.Item id={key} textValue={title}>
                              {title}
                            </ListBox.Item>
                          )}
                        </ListBox>
                      </Select.Popover>
                    </Select>
                  )}
                />

                <Controller
                  control={control}
                  name="label"
                  render={({ field }) => (
                    <TextField
                      value={field.value ?? ""}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                    >
                      <Label>Метка (label)</Label>
                      <Input ref={field.ref} placeholder="Введите метку" />
                    </TextField>
                  )}
                />

                <Controller
                  control={control}
                  name="url"
                  render={({ field }) => (
                    <TextField
                      value={field.value ?? ""}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                    >
                      <Label>URL</Label>
                      <Input ref={field.ref} placeholder="https://..." />
                    </TextField>
                  )}
                />

                <Controller
                  control={control}
                  name="image"
                  render={({ field }) => (
                    <TextField
                      value={field.value ?? ""}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                    >
                      <Label>Изображение (URL)</Label>
                      <Input ref={field.ref} placeholder="https://..." />
                    </TextField>
                  )}
                />

                <div className="flex items-center gap-3">
                  <Switch
                    isSelected={isActive}
                    onChange={(value: boolean) => setValue("isActive", value)}
                  >
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    <Switch.Content>
                      <Label>Активен</Label>
                    </Switch.Content>
                  </Switch>
                </div>
              </Drawer.Body>

              <Drawer.Footer>
                <Button
                  variant="danger-soft"
                  onPress={() => onOpenChange(false)}
                >
                  Отмена
                </Button>
                <Button isPending={isPending} type="submit" variant="primary">
                  {mode === "create" ? "Создать" : "Сохранить"}
                </Button>
              </Drawer.Footer>
            </Form>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
